import { BlockedTenantsApi, OrganizationsApi, OrganizationUserDTO, UserDTO, UsersApi } from '@reposit/api-client';
import { AxiosResponse } from 'axios';
import { chain, get, isNil } from 'lodash';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { FlashState } from '../../components/FlashMessage';
import { getErrorMessage } from '../../utils/common.utils';
import { getCurrentOrganizationId } from '../account/account.selectors';
import { syncEntitiesAndGetResults } from '../entities/entities.sagas';
import { FlashMessagesActionTypes, setFlashMessage } from '../flash-messages/flash-messages.actions';
import { setCurrentModal } from '../modal/modal.actions';
import { FetchOrganizationUserPayload } from '../organization-user/organization-user.types';
import SCHEMA from '../schema';
import { createBlockedTenantsApi, createOrganizationsApi, createUsersApi, runSagaWithAuth } from '../utils/api.utils';
import {
  BLOCK_TENANT_API_REQUESTED,
  BLOCK_TENANT_STORE_KEY,
  blockTenantFailed,
  blockTenantSuccess,
  FETCH_BLOCKED_TENANT_API_REQUESTED,
  FETCH_BLOCKED_TENANT_STORE_KEY,
  FETCH_ORGANIZATION_USER_BY_USER_ID_API_REQUESTED,
  FETCH_ORGANIZATION_USERS_API_REQUESTED,
  FETCH_USER_BY_ID_API_REQUESTED,
  fetchBlockedTenantFailed,
  fetchBlockedTenantSuccess,
  fetchOrganizationUsersByUserIdFailed,
  fetchOrganizationUsersByUserIdSuccess,
  fetchOrganizationUsersFailed,
  fetchOrganizationUsersSuccess,
  fetchUserByIdFailed,
  fetchUserByIdSuccess,
  RESET_USER_PASSWORD_API_REQUESTED,
  RESET_USER_PASSWORD_STORE_KEY,
  resetUserPasswordFailed,
  resetUserPasswordSuccess,
  setIsEditModalOpen,
  UNBLOCK_TENANT_API_REQUESTED,
  UNBLOCK_TENANT_STORE_KEY,
  UPDATE_USER_BY_ID_API_REQUESTED,
  UPDATE_USER_BY_ID_STORE_KEY,
  UserActionTypes,
  unblockTenantFailed,
  unblockTenantSuccess,
  updateUserByIdFailed,
  updateUserByIdSuccess,
} from './user.actions';
import {
  BlockTenantPayload,
  FetchOrganizationUsersByUserIdPayload,
  FetchUserByIdPayload,
  ResetPasswordPayload,
  UnblockTenantPayload,
  UpdateUserPayload,
} from './user.types';

// ****************
// WORKERS
// ****************

export function* fetchUsersForOrganization({ payload }: { type: string; payload: FetchOrganizationUserPayload }) {
  try {
    const currentOrganizationId: string = yield select(getCurrentOrganizationId);
    const organizationsApi: OrganizationsApi = yield createOrganizationsApi();
    const orgId = payload.organizationId || currentOrganizationId;

    const apiResponse: AxiosResponse<UserDTO[]> = yield call(
      runSagaWithAuth(() => organizationsApi.fetchUsersByOrganizationId(orgId)),
    );
    const ids: string[] = yield syncEntitiesAndGetResults(apiResponse.data, SCHEMA.users);
    yield put<UserActionTypes>(fetchOrganizationUsersSuccess(ids));
  } catch (e) {
    yield put<UserActionTypes>(fetchOrganizationUsersFailed(get(e, 'response.data.message', e)));
  }
}

export function* fetchUserById({ payload }: { type: string; payload: FetchUserByIdPayload }) {
  try {
    const usersApi: UsersApi = yield createUsersApi();
    const apiResponse: AxiosResponse<UserDTO[]> = yield call(runSagaWithAuth(() => usersApi.findOneByID(payload.userId)));

    yield syncEntitiesAndGetResults(apiResponse.data, SCHEMA.user);

    yield put<UserActionTypes>(fetchUserByIdSuccess());
  } catch (e) {
    yield put<UserActionTypes>(fetchUserByIdFailed(get(e, 'response.data.message', e)));
  }
}

export function* resetUserPassword({ payload }: { type: string; payload: ResetPasswordPayload }) {
  try {
    const usersApi: UsersApi = yield createUsersApi();
    yield call(runSagaWithAuth(() => usersApi.resetUserPassword(payload.userId)));

    yield put<UserActionTypes>(resetUserPasswordSuccess());

    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: RESET_USER_PASSWORD_STORE_KEY,
        message: `Success! Reset password email sent`,
        state: FlashState.SUCCESS,
      }),
    );
    yield put(setCurrentModal(''));
  } catch (e) {
    const error = getErrorMessage(e);
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({ key: RESET_USER_PASSWORD_STORE_KEY, message: error, state: FlashState.ERROR }),
    );
    yield put<UserActionTypes>(resetUserPasswordFailed(get(e, 'response.data.message', e)));
  }
}

export function* fetchOrganizationUsersByUserId({ payload }: { type: string; payload: FetchOrganizationUsersByUserIdPayload }) {
  try {
    const usersApi: UsersApi = yield createUsersApi();
    const apiResponse: AxiosResponse<OrganizationUserDTO[]> = yield call(
      runSagaWithAuth(() => usersApi.getOrganizationUsers(payload.userId)),
    );
    yield syncEntitiesAndGetResults(apiResponse.data, SCHEMA.organizationUsers);
    yield put<UserActionTypes>(fetchOrganizationUsersByUserIdSuccess());
  } catch (e) {
    yield put<UserActionTypes>(fetchOrganizationUsersByUserIdFailed(get(e, 'response.data.message', e)));
  }
}

const isEmptyString = (val: any) => {
  return val === '';
};

export function* updateUserById({ payload }: { type: string; payload: UpdateUserPayload }) {
  try {
    const usersApi: UsersApi = yield createUsersApi();
    const apiResponse: AxiosResponse<UserDTO[]> = yield call(
      runSagaWithAuth(() => usersApi.updateUser(payload.userId, chain(payload.user).omitBy(isNil).omitBy(isEmptyString).value())),
    );
    yield syncEntitiesAndGetResults(apiResponse.data, SCHEMA.user);
    yield put(setIsEditModalOpen(false));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: UPDATE_USER_BY_ID_STORE_KEY,
        message: `Success! User updated`,
        state: FlashState.SUCCESS,
      }),
    );
    yield put<UserActionTypes>(updateUserByIdSuccess());
  } catch (e) {
    const error = getErrorMessage(e);
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({ key: UPDATE_USER_BY_ID_STORE_KEY, message: error, state: FlashState.ERROR }),
    );
    yield put<UserActionTypes>(updateUserByIdFailed(error));
  }
}

export function* blockTenant({ payload }: { type: string; payload: BlockTenantPayload }) {
  try {
    const blockedTenantsApi: BlockedTenantsApi = yield createBlockedTenantsApi();
    const apiResponse: AxiosResponse = yield call(
      runSagaWithAuth(() =>
        blockedTenantsApi.createBlockedTenant({ email: payload.email, reason: payload.reason, notes: payload.notes }),
      ),
    );

    yield put<UserActionTypes>(blockTenantSuccess(apiResponse.data));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: BLOCK_TENANT_STORE_KEY,
        message: `Success! Tenant ${payload.email} has been blocked`,
        state: FlashState.SUCCESS,
      }),
    );
    yield put(setCurrentModal(''));
  } catch (e) {
    const error = getErrorMessage(e);
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({ key: BLOCK_TENANT_STORE_KEY, message: error, state: FlashState.ERROR }),
    );
    yield put<UserActionTypes>(blockTenantFailed(error));
  }
}

export function* fetchBlockedTenant({ payload }: { type: string; payload: { email: string } }) {
  try {
    const blockedTenantsApi: BlockedTenantsApi = yield createBlockedTenantsApi();
    const apiResponse: AxiosResponse = yield call(runSagaWithAuth(() => blockedTenantsApi.getBlockedTenant(payload.email)));
    yield put<UserActionTypes>(fetchBlockedTenantSuccess(apiResponse.data, payload.email));
  } catch (e) {
    if (get(e, 'response.status') === 404) {
      yield put<UserActionTypes>(fetchBlockedTenantSuccess(null, payload.email));
      return;
    }
    const error = getErrorMessage(e);
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({ key: FETCH_BLOCKED_TENANT_STORE_KEY, message: error, state: FlashState.ERROR }),
    );
    yield put<UserActionTypes>(fetchBlockedTenantFailed(error, payload.email));
  }
}

export function* unblockTenant({ payload }: { type: string; payload: UnblockTenantPayload }) {
  try {
    const blockedTenantsApi: BlockedTenantsApi = yield createBlockedTenantsApi();
    yield call(runSagaWithAuth(() => blockedTenantsApi.deleteBlockedTenant(payload.email)));

    yield put<UserActionTypes>(unblockTenantSuccess(payload.email));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: UNBLOCK_TENANT_STORE_KEY,
        message: `Success! Tenant ${payload.email} has been unblocked`,
        state: FlashState.SUCCESS,
      }),
    );
    yield put(setCurrentModal(''));
  } catch (e) {
    const error = getErrorMessage(e);
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({ key: UNBLOCK_TENANT_STORE_KEY, message: error, state: FlashState.ERROR }),
    );
    yield put<UserActionTypes>(unblockTenantFailed(error));
  }
}

// ****************
// WATCHERS
// ****************
export function* watchUserSagas() {
  yield takeLatest(FETCH_ORGANIZATION_USERS_API_REQUESTED, fetchUsersForOrganization);
  yield takeLatest(FETCH_USER_BY_ID_API_REQUESTED, fetchUserById);
  yield takeLatest(RESET_USER_PASSWORD_API_REQUESTED, resetUserPassword);
  yield takeLatest(FETCH_ORGANIZATION_USER_BY_USER_ID_API_REQUESTED, fetchOrganizationUsersByUserId);
  yield takeLatest(UPDATE_USER_BY_ID_API_REQUESTED, updateUserById);
  yield takeLatest(BLOCK_TENANT_API_REQUESTED, blockTenant);
  yield takeLatest(FETCH_BLOCKED_TENANT_API_REQUESTED, fetchBlockedTenant);
  yield takeLatest(UNBLOCK_TENANT_API_REQUESTED, unblockTenant);
}
